import { FC, useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { DsmButton, DsmGrid, DsmIcon } from '@dsm-dcs/design-system-react';
import { v4 as uuidv4 } from 'uuid';
import { FormType } from '../../common';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { processAndStageStylesV2 } from '../../../../../../_metronic/layout';
import { CSSClassesList } from '../../../../helpers/helperTypes';
import ReactHookDsmSelect from '../../../../../modules/Helpers/ReactHookDsmSelect2';
import { Spacer } from '../../../helpers/Spacer';
import ShrimpPackagingTypeComponent from './ShrimpPackagingTypeComponent';
import { PackagingTypes, ShrimpBaseline } from '../../../../models/Baseline/ShrimpBaseline';
import { PrimaryPackagingType, SecondaryPackagingType, TertiaryPackagingType } from '../../../../../../graphql/generated/blonk/shrimp';

export interface OutputOption {
    value: string;
    text: string;
}

interface ShrimpPackagingCategoryComponentProps {
    formType: FormType;
    fieldItemPrefix: string;
    outputOptions: OutputOption[];
}

const ShrimpPackagingCategoryComponent: FC<ShrimpPackagingCategoryComponentProps> = ({
    formType,
    fieldItemPrefix,
    outputOptions,
}) => {
    const intl = useIntl();
    const classes = processAndStageStylesV2() as CSSClassesList;
    const { control } = useFormContext<ShrimpBaseline>();
    const fc = useFormContext<ShrimpBaseline>();
    const [disableAddPackagingButton, setDisableAddPackagingButton] = useState<boolean>(false);
    const packagingTypes: PackagingTypes[] = ['primary', 'secondary', 'tertiary'];

    const {
        fields: primaryPackaging,
        append: addPrimaryPackaging,
        remove: removePrimaryPackaging,
    } = useFieldArray({
        name: `${fieldItemPrefix}.primary`,
        control,
        keyName: "keyId",
    });

    const {
        fields: secondaryPackaging,
        append: addSecondaryPackaging,
        remove: removeSecondaryPackaging,
    } = useFieldArray({
        name: `${fieldItemPrefix}.secondary`,
        control,
        keyName: "keyId",
    });

    const {
        fields: tertiaryPackaging,
        append: addTertiaryPackaging,
        remove: removeTertiaryPackaging,
    } = useFieldArray({
        name: `${fieldItemPrefix}.tertiary`,
        control,
        keyName: "keyId",
    });

    const packagingFieldArrays = {
        primary: primaryPackaging,
        secondary: secondaryPackaging,
        tertiary: tertiaryPackaging,
    };

    const packagingTypeLimits = {
        primary: Object.keys(PrimaryPackagingType).length,
        secondary: Object.keys(SecondaryPackagingType).length,
        tertiary: Object.keys(TertiaryPackagingType).length,
    };

    const addPackaging = (type: PackagingTypes) => {
        const newItem = {
            id: uuidv4(),
            type: '',
            amount: '',
        };

        switch (type) {
            case "primary": {
                addPrimaryPackaging(newItem);
                break;
            }
            case "secondary": {
                addSecondaryPackaging(newItem);
                break;
            }
            case "tertiary": {
                addTertiaryPackaging(newItem);
                break;
            }
        }
    };

    const removePackaging = (index: number, type: PackagingTypes) => {
        switch (type) {
            case "primary": {
                removePrimaryPackaging(index);
                break;
            }
            case "secondary": {
                removeSecondaryPackaging(index);
                break;
            }
            case "tertiary": {
                removeTertiaryPackaging(index);
                break;
            }
        }
    };

    const items = useWatch({
        name: fieldItemPrefix.replace(/\.\d+$/, ""),
        control: fc.control,
    });

    const getAvailableOptions = (): OutputOption[] => {
        if (!Array.isArray(items)) return [];

        const currentValue = fc.getValues(`${fieldItemPrefix}.outputId`);

        const unavailableOutputs = items
            .map((item) => item?.outputId)
            .filter(Boolean);

        const filteredOptions = outputOptions.filter((option) => {
            const isUsed = unavailableOutputs.includes(option.value);
            const isCurrent = option.value === currentValue;

            return !isUsed || isCurrent;
        });

        return [
            {
                value: '',
                text: intl.formatMessage({
                    id: 'SUSTELL.STAGES.PROCESSING.PACKAGING.EMPTY_OUTPUT'
                })
            },
            ...filteredOptions
        ];
    };

    const selectedOutput = useWatch({
        name: `${fieldItemPrefix}.outputId`,
        control
    });

    useEffect(() => {
        if (!selectedOutput)
            setDisableAddPackagingButton(true);
        else
            setDisableAddPackagingButton(false);
    }, [selectedOutput]);

    useEffect(() => {
        packagingTypes.forEach((type) => {
            if (packagingFieldArrays[type].length === 0) {
                addPackaging(type);
            }
        });
    }, []);

    return (
        <>
            <DsmGrid className={classes.dsmGridTwoColumn}>
                <ReactHookDsmSelect
                    label={intl.formatMessage({ id: 'SUSTELL.STAGES.PROCESSING.PACKAGING.OUTPUT_NAME' })}
                    name={`${fieldItemPrefix}.outputId`}
                    options={getAvailableOptions()}
                    disabled={formType === FormType.View}
                    defaultValue={fc.getValues(`${fieldItemPrefix}.outputId`)}
                    tooltip={intl.formatMessage({ id: 'SUSTELL.STAGES.PROCESSING.PACKAGING.OUTPUT_NAME.SHRIMP.TT' })}
                />
            </DsmGrid>
            <Spacer height="24px" />

            {packagingTypes.map((type, index) => (
                <div key={type}>
                    {packagingFieldArrays[type].map((field, fieldIndex) => (
                        <DsmGrid
                            key={field.id}
                            className={
                                packagingFieldArrays[type].length > 1 ?
                                    classes.dsmGridTwoColumnAndButton :
                                    classes.dsmGridTwoColumn
                            }
                        >
                            <ShrimpPackagingTypeComponent
                                formType={formType}
                                fieldItemPrefix={`${fieldItemPrefix}.${type}.${fieldIndex}`}
                                unavailableOptions={
                                    packagingFieldArrays[type]
                                        .map(
                                            (item) => item.type
                                        ) || []
                                }
                                packagingType={type}
                            />

                            {formType !== FormType.View && packagingFieldArrays[type].length > 1 && (
                                <DsmButton
                                    variant="text"
                                    iconOnly
                                    destructive
                                    style={{ padding: '0px', marginTop: '24px' }}
                                    onClick={() => removePackaging(fieldIndex, type)}
                                >
                                    <DsmIcon slot="before" name="general/trash-01" />
                                </DsmButton>
                            )}
                        </DsmGrid>
                    ))}

                    <DsmButton
                        variant="text"
                        onClick={() => addPackaging(type)}
                        disabled={
                            formType === FormType.View ||
                            packagingFieldArrays[type].length >= packagingTypeLimits[type] ||
                            disableAddPackagingButton
                        }
                    >
                        <DsmIcon slot="before" name="general/plus-circle" />
                        {intl.formatMessage({
                            id: `SUSTELL.STAGES.PROCESSING.PACKAGING.ADD_ANOTHER_${type.toUpperCase()}_PACKAGING_TYPE`,
                        })}
                    </DsmButton>
                    {index < packagingTypes.length - 1 && <Spacer height="24px" />}
                </div>
            ))}
        </>
    );
};

export default ShrimpPackagingCategoryComponent;
